import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getTokens, onAuthChange, signOut as apiSignOut } from "../api/client";
import { getMe } from "../api/endpoints";
import type { User } from "../api/types";

interface AuthState {
  user: User | null;
  loading: boolean;
  signedIn: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [signedIn, setSignedIn] = useState(() => getTokens() !== null);
  // Start loading only if there is a session to resolve; otherwise the login
  // page would flash a spinner before rendering.
  const [loading, setLoading] = useState(signedIn);

  const loadUser = useCallback(async () => {
    if (!getTokens()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await getMe());
    } catch {
      // The client already clears the session when it is truly unrecoverable,
      // so a failure here just means we have no profile to show.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
    return onAuthChange((tokens) => {
      setSignedIn(tokens !== null);
      if (tokens) void loadUser();
      else setUser(null);
    });
  }, [loadUser]);

  const signOut = useCallback(async () => {
    await apiSignOut();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, signedIn, signOut, refreshUser: loadUser }),
    [user, loading, signedIn, signOut, loadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
